/**
 * The top bar's data model, kept out of the component so it can be tested, as the rail's is
 * (`sidebar/definitions.js`).
 *
 * The bar says where the admin is, as a trail of breadcrumbs, and who they are, as an avatar
 * of their initials beside their email. Everything here is worked out from what the frame
 * already has: the menu the server built, the current route, the tenant state and the
 * `loggedInUser` the config carries (`server/app/configApi.js`). Nothing new is fetched.
 */
import startCase from 'lodash/startCase';
import { homeItem, menuKey, resolveLabel, toGroups } from '../sidebar/definitions';

/**
 * The avatar's letters, from the name part of the email: `pat.reyes@…` is PR, and a name
 * with no separator gives its first two letters, so `rweaver@…` is RW. Admin accounts carry
 * an email and no name, so the email is all there is to go on. Empty when there is no email,
 * and the avatar then draws a person icon instead.
 */
export const initialsFor = (email) => {
  const name = String(email ?? '').split('@')[0];
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '').slice(0, 2);
  return letters.toUpperCase();
};

/** A role as the admin reads it: `super_admin` is Super Admin. */
export const roleLabel = (role) => (role ? startCase(role) : '');

/**
 * Where the tenant crumb goes: the NavBar, which is where the logo goes too, or failing that
 * the item the tenant opens on. Only an item that routes somewhere: a tenant whose home item
 * is Export Content would otherwise run an export from a breadcrumb.
 */
export const homeTarget = (menu = []) =>
  homeItem(menu) || menu.find((item) => item?.isHomePage && item.navigation?.state) || null;

/**
 * Change Password, if this tenant's menu offers it. The account menu shows it only then: a
 * tenant whose admins sign in elsewhere leaves it out of its menu for a reason. The href is
 * the item as a server from before SURF-4828 sends it.
 */
export const changePasswordItem = (menu = []) =>
  menu.find((item) => ['main.change-password', '/changePassword'].includes(menuKey(item))) || null;

/**
 * Whether the route is the item's own screen, rather than one of its sub-pages (an editor, a
 * record). A plugin item names one page of one plugin with no params; a built-in screen is
 * one state, and the server merges a `pageName` into its params that its route never carries,
 * so a built-in is matched on the state alone (as `selectedItem` does).
 */
export const isItemScreen = (item, route = {}) => {
  const nav = item?.navigation;
  if (!nav?.state || nav.state !== route.stateName) return false;
  if (nav.state !== 'main.plugin') return true;
  const own = nav.stateParams || {};
  const here = route.stateParams || {};
  return (
    own.pluginName === here.pluginName &&
    (own.pageName || 'default') === (here.pageName || 'default') &&
    String(own.params ?? '') === String(here.params ?? '')
  );
};

/**
 * The screens in a rail group, for its crumb's menu: the items that go somewhere. Export
 * Content and Preview App are actions, not places, and are left out.
 */
export const groupScreens = (items = []) => items.filter((item) => item.navigation?.state);

/** Two labels that say the same thing, ignoring case and spacing. */
const sameText = (a, b) =>
  String(a ?? '')
    .trim()
    .toLowerCase() ===
  String(b ?? '')
    .trim()
    .toLowerCase();

/** Whether a back link's target is the rail item's own screen. */
const backIsItem = (back, item) => {
  const target = back?.target;
  const own = item?.navigation;
  if (!target || own?.state !== 'main.plugin') return false;
  const params = own.stateParams || {};
  return (
    target.pluginName === params.pluginName &&
    (target.pageName || 'default') === (params.pageName || 'default') &&
    String(target.params ?? '') === String(params.params ?? '')
  );
};

/**
 * The rail's sections as the breadcrumbs' top tier: every labelled group with a screen to go
 * to, in the rail's order, each with its screens. A group of nothing but actions (Export
 * Content, Preview App) has nowhere to go and is left out, and so is a group with no name.
 */
export const menuSections = (menu = [], messages) =>
  toGroups(menu)
    .filter((group) => group.label)
    .map((group) => ({
      group,
      label: resolveLabel(messages, group.label),
      items: groupScreens(group.items),
    }))
    .filter((section) => section.items.length > 0);

/**
 * The trail, from the tenant down to the page. Each level opens a menu of what sits beside
 * it, as a file browser's path bar does:
 *
 *   tenant   the tenant, and the subtenant when the caller names one. Goes home.
 *   group    the rail group the screen is in: the top tier. It opens every section, each
 *            showing its screens, so any screen is two moves away. `sections`, and only
 *            when there is more than one; otherwise it is a plain label.
 *   item     the rail item the page belongs to: the second tier. It opens the screens of its
 *            own section (`items`, when there is more than one) with itself ticked; with none
 *            beside it, it goes to that screen, unless it is this one.
 *   parent   the record the page's back link goes to, when that is not the item's screen:
 *            the collection above one of its items. Goes back there.
 *   page     the page's own title, on a sub-page, when it says something the crumbs before
 *            it do not.
 *
 * `back` is the page's back link (`pageBack` in `shared/cmsPage/definitions.js`), which the
 * breadcrumbs replace. When it goes to the item's own screen, by its target or by name, the
 * item crumb goes back through it instead, so the list opens where the admin left it
 * (SURF-4843) rather than fresh, as it does from the rail. Otherwise it is the parent crumb.
 *
 * `current` marks where the admin is, which is not a link. A crumb has `target` (a menu item,
 * for the frame's `onNavigate`) or `onClick` (the page's own way back), or neither.
 */
export const breadcrumbTrail = ({
  menu = [],
  messages,
  route = {},
  selected = null,
  tenant = '',
  subtenant = '',
  pageTitle = '',
  back = null,
}) => {
  const label = (text) => resolveLabel(messages, text);
  const title = String(pageTitle || '').trim();
  const backName = String(back?.label || '').trim();
  const crumbs = [];

  if (tenant) {
    crumbs.push({
      kind: 'tenant',
      label: subtenant ? `${tenant} · ${subtenant}` : tenant,
      target: homeTarget(menu),
    });
  }

  let parent = null;
  if (selected) {
    const group = toGroups(menu).find((candidate) => candidate.items.includes(selected));
    if (group?.label) {
      const sections = menuSections(menu, messages).map((section) => ({
        label: section.label,
        items: section.items,
        // `toGroups` builds new objects on every call, so the section is known by its items.
        current: section.group.items.includes(selected),
      }));
      crumbs.push({
        kind: 'group',
        label: label(group.label),
        ...(sections.length > 1 && { sections }),
      });
    }
    const itemLabel = label(selected.text);
    const onItem = isItemScreen(selected, route);
    const item = { kind: 'item', label: itemLabel, target: selected, current: onItem };
    const siblings = group ? groupScreens(group.items) : [];
    if (siblings.length > 1) item.items = siblings;
    const backToItem = backName && (backIsItem(back, selected) || sameText(backName, itemLabel));
    if (backToItem && !onItem) item.onClick = back.onClick;
    else if (backName && !backToItem)
      parent = { kind: 'parent', label: backName, onClick: back.onClick };
    // On the item's own screen a back link goes somewhere above it, so its crumb comes first.
    if (parent && onItem) {
      crumbs.push(parent);
      parent = null;
    }
    crumbs.push(item);
    if (parent) crumbs.push(parent);
    const said = [itemLabel, parent?.label];
    if (!onItem && title && !said.some((text) => sameText(text, title))) {
      crumbs.push({ kind: 'page', label: title, current: true });
    }
  } else {
    if (backName) {
      parent = { kind: 'parent', label: backName, onClick: back.onClick };
      crumbs.push(parent);
    }
    if (title && !sameText(title, parent?.label)) {
      crumbs.push({ kind: 'page', label: title, current: true });
    }
  }

  return crumbs;
};
